import { instance as store } from 'app/store'
import { hydrate } from 'app/store/local'
import find from 'lodash/find'
import get from 'lodash/get'
import once from 'lodash/once'
import debounce from 'lodash/debounce'
import { update } from 'app/metaSocial'

function anyTrue(to, prop) {
  return find([...to.matched].map((t) => t.meta[prop]))
}

function isSupportEnabled() {
  return store.getters['permits/has']('humanSupport')
}

export const ensureLocalsLoaded = (vueInstance) => async (to, from, next) => {
  await hydrate(store)

  next()
}

export const ensureSession = (vueInstance) => async (to, from, next) => {
  const isSignedIn = store.getters['sessions/isSignedIn']
  const account = store.getters['sessions/account']

  if (!isSignedIn) {
    try {
      await store.dispatch('sessions/fetchCurrentSession')

      to.meta.isSignedIn = true
      next()
    } catch (e) {
      to.meta.isSignedIn = false
      next()
    }
  } else {
    to.meta.isSignedIn = isSignedIn
    to.meta.account = account
    next()
  }
}

export const ensureSignedOut = (vueInstance) => async (to, from, next) => {
  const { isSignedIn } = to.meta
  const denyAuth = anyTrue(to, 'denyAuth')

  if (isSignedIn && denyAuth) {
    try {
      await store.dispatch('sessions/signOut')
    } finally {
      next()
    }
  } else {
    next()
  }
}

export const ensureSignedIn = (vueInstance) => async (to, from, next) => {
  const requiresAdmin = anyTrue(to, 'requiresAdmin')
  const requiresAuth = requiresAdmin ? true : anyTrue(to, 'requiresAuth')

  if (requiresAuth) {
    const { isSignedIn } = to.meta

    if (isSignedIn) {
      if (requiresAdmin) {
        const isAdmin = store.getters['accounts/isAdmin']

        if (isAdmin) {
          next()
        } else {
          next({ path: '/sessions/sign-in', query: { next: to.fullPath } })
        }
      } else {
        next()
      }
    } else {
      next({ path: '/sessions/sign-in', query: { next: to.fullPath } })
    }
  } else {
    next()
  }
}

const scrollTo = (to) => {
  function _s() {
    const target = to.hash.substr(1)
    let element = null

    // If the scroll changes before the initial attempt the user is either
    // interacting with the page or a saved scroll position is being used.
    // In this case we need to abort, we don't want to jack with something
    // the user has done by themselves.
    if (window.scrollY !== 0) {
      return false
    }

    if (target) {
      element = document.getElementById(target)
    }

    if (element && typeof element.scrollIntoViewIfNeeded === 'function') {
      element.scrollIntoViewIfNeeded()
      return true
    } else if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView()
      return true
    } else {
      return false
    }
  }

  // This will first attempt to scroll directly to the element on the page if
  // it's available. However, in a lot of cases the content is waiting on an
  // API call or some other delayed action and the Element ID may not be
  // available yet. In this case we detect the failure (returns false) and then
  // schedule another go at it a half a second later.
  return function () {
    if (!_s()) {
      setTimeout(_s, 500)
    }
  }
}

export const initialScrollWithHash = (vueInstance) =>
  once((to) => {
    vueInstance.nextTick(scrollTo(to))
  })

export const initializeBeacon = (vueInstance) => () => {
  // disable help scout on search to improve load time
  if (isSupportEnabled() && window.Beacon && window.location.pathname !== '/search') {
    window.Beacon('config', { display: { style: 'iconAndText', text: 'Help' } })
  }
}

const fetchMetaSocialDebounced = debounce(
  (fullPath) => {
    store.dispatch('metaSocial/fetch', fullPath).then(update)
  },
  3000,
  { leading: true }
)

export const metaSocialLoad = (vueInstance) => (to, from, next) => {
  if (from.matched.length > 0) {
    fetchMetaSocialDebounced(to.fullPath)
  }
  next()
}

// We are disabling this feature until we are able to get a better fix in.
export const loadDiscount = (vueInstance) => async (to, from, next) => {
  const discount = get(to, ['query', 'discount'], null)

  if (discount) {
    store.dispatch('discounts/setDiscount', discount)
  }
  next()
}
